import { inject, Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { firstValueFrom } from 'rxjs';
import { StingrayAuthenticationService } from '@app/core/authentication/stingray-authentication.service';
import { environment } from '../../../environments/environment';
import { ChatService } from './chat.service';

/**
 * Keeps a SignalR connection to the agent's pending-runs hub, which pushes a signal whenever the documents the
 * assistant prepared (and waits on an answer for) change: a run reaches its first question, is answered, ends, or is
 * taken on by a colleague. Each signal reloads the pending-runs list, so the badge on the assistant button shows up
 * the moment a document is ready instead of on the next poll.
 *
 * The hub carries no data, only the hint; the list itself is still fetched with the usual authorisation. The
 * connection reconnects by itself and reloads the list after every (re)connect, since signals sent meanwhile are lost.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowRunsHubService {

  private static readonly hubPath = '/hubs/workflow-runs';
  private static readonly pendingRunsChanged = 'pendingRunsChanged';

  private chatService = inject(ChatService);
  private authenticationService = inject(StingrayAuthenticationService);
  private connection?: HubConnection;

  /** Connects (once) and keeps the pending-runs list in sync with the agent's signals. */
  connect(): void {
    if (this.connection) {
      return;
    }

    this.connection = new HubConnectionBuilder()
      .withUrl(`${environment.agentApiBaseUrl}${WorkflowRunsHubService.hubPath}`, {
        // A WebSocket carries no Authorization header; the token travels as access_token in the query string.
        accessTokenFactory: () => this.accessToken(),
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    this.connection.on(WorkflowRunsHubService.pendingRunsChanged, () => this.chatService.loadPendingRuns());
    this.connection.onreconnected(() => this.chatService.loadPendingRuns());

    this.start();
  }

  private start(): void {
    this.connection.start()
      .then(() => this.chatService.loadPendingRuns())
      .catch((error) => {
        // Not reachable right now: the fallback refresh keeps the badge roughly right; try again in a while.
        console.warn('Pending-runs hub connection failed, retrying', error);
        setTimeout(() => {
          if (this.connection?.state === HubConnectionState.Disconnected) {
            this.start();
          }
        }, 30_000);
      });
  }

  private async accessToken(): Promise<string> {
    // getTokenSilently$ resolves to the raw token string (no detailed response is requested), as the HTTP
    // interceptor relies on as well.
    const token = await firstValueFrom(this.authenticationService.getTokenSilently$());
    return token as unknown as string;
  }
}
